import {
  ONBOARDING_BOOKING_KEY,
  ONBOARDING_SUBSESSION_ID,
} from "./utils";

/**
 * The link that gets shared with mediums. One stable path; everything past the
 * gate page is internal and can change without reissuing links.
 */
export const buildOnboardingShareLink = (hostId) => {
  return `${window.location.origin}/onboarding-booking/${hostId}`;
};

/**
 * Where the gate page forwards a logged-in medium: the existing booking calendar,
 * deep linked to the Onboarding Session with the host pre-selected.
 */
export const buildOnboardingCalendarPath = (hostId) => {
  return `/booking?to=${ONBOARDING_BOOKING_KEY}&session_length=${ONBOARDING_SUBSESSION_ID}&host=${hostId}&calendar=true`;
};

export const buildOnboardingLoginPath = (hostId) => {
  return `/login?redirect_to=onboarding-booking&host=${hostId}`;
};

/**
 * Single decision for the gate page: log in, show the calendar, or explain why not.
 * Kept pure so every state is unit testable without rendering the app shell.
 */
export const resolveOnboardingEntry = ({ hasToken, role, hostId }) => {
  if (!hostId) {
    return { action: "blocked", reason: "invalid-link" };
  }

  if (!hasToken) {
    return { action: "login", target: buildOnboardingLoginPath(hostId) };
  }

  if (role === "medium") {
    return { action: "calendar", target: buildOnboardingCalendarPath(hostId) };
  }

  if (!role) {
    return { action: "wait" };
  }

  return { action: "blocked", reason: "not-a-medium" };
};

/**
 * Sort out what the host-info call actually said. The API layer resolves failures with
 * the error object rather than rejecting, so a 401 or a 500 arrives here looking nothing
 * like a payload - without this, a dead session gets reported as a dead booking link.
 */
export const classifyHostInfoResponse = (response) => {
  const payload = response && response.data;

  if (!payload || typeof payload.status === "undefined") {
    return { kind: "unavailable" };
  }

  if (!payload.status) {
    return { kind: "rejected", message: payload.message };
  }

  if (payload.data && payload.data.already_booked) {
    return {
      kind: "already-booked",
      appointment: payload.data.existing_appointment,
    };
  }

  return { kind: "ok", host: payload.data };
};

/**
 * Whether a /booking visit came from the onboarding link. Medium route trees that have
 * no booking hub of their own allow the booking page on this basis only.
 */
export const isOnboardingBookingRequest = (searchParams) => {
  return !!searchParams && searchParams.get("to") === ONBOARDING_BOOKING_KEY;
};

export const ONBOARDING_HOST_STORAGE_KEY = "onboarding_booking_host";
export const ONBOARDING_REDIRECT_VALUE = "onboarding-booking";

/**
 * Remember the host across the login round trip, so a logged-out medium lands back
 * on the calendar they clicked rather than on their default home screen.
 */
export const rememberOnboardingHost = (hostId) => {
  if (hostId) {
    localStorage.setItem(ONBOARDING_HOST_STORAGE_KEY, String(hostId));
  }
};

export const takeRememberedOnboardingHost = () => {
  const hostId = localStorage.getItem(ONBOARDING_HOST_STORAGE_KEY);
  localStorage.removeItem(ONBOARDING_HOST_STORAGE_KEY);
  return hostId;
};
